import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Receipt, RotateCw, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const EUR = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);
const DT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};
const DTSHORT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const map = {
    active: { bg: "bg-[#00E575]/15 border-[#00E575]/50 text-[#00E575]", label: "Aktiv" },
    trialing: { bg: "bg-blue-500/15 border-blue-500/50 text-blue-300", label: "Trial" },
    expired: { bg: "bg-amber-500/15 border-amber-500/50 text-amber-300", label: "Abgelaufen" },
    cancelled: { bg: "bg-zinc-500/15 border-zinc-500/50 text-zinc-300", label: "Gekündigt" },
  };
  const m = map[status] || { bg: "bg-zinc-700 text-zinc-300", label: status || "—" };
  return <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-bold uppercase tracking-widest border ${m.bg}`}>{m.label}</span>;
};

export default function MyBilling() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/billing/me").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const daysLeft = data?.subscription_end_at
    ? Math.max(0, Math.ceil((new Date(data.subscription_end_at) - Date.now()) / 86400000))
    : null;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-6 md:py-8 pb-24">
        <p className="font-mono text-[12px] text-[#00E575] tracking-[0.3em] uppercase mb-2">Account</p>
        <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mb-6">Mein Abo</h1>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin inline text-[#00E575]" size={32} /></div>
        ) : !data ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-6 text-center text-zinc-400">Konnte Daten nicht laden.</div>
        ) : (
          <>
            {/* Active subscription card */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="font-mono text-[12px] text-zinc-300 tracking-widest uppercase">Aktueller Plan</div>
                  <h2 className="font-display text-2xl font-bold">{data.plan_label || "—"}-Abo</h2>
                  <div className="text-sm text-zinc-400 mt-0.5">{EUR(data.monthly_eur)} / Monat</div>
                </div>
                <StatusBadge status={data.subscription_status} />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-zinc-800">
                <div>
                  <div className="text-[12px] font-mono uppercase tracking-widest text-zinc-300 mb-1">Gestartet am</div>
                  <div className="text-sm text-zinc-200">{DTSHORT(data.subscription_started_at)}</div>
                </div>
                <div>
                  <div className="text-[12px] font-mono uppercase tracking-widest text-zinc-300 mb-1">Läuft bis</div>
                  <div className="text-sm text-zinc-200">{DTSHORT(data.subscription_end_at)}</div>
                  {daysLeft != null && data.subscription_status === "active" && (
                    <div className={`text-[12px] mt-0.5 ${daysLeft < 30 ? "text-amber-300" : "text-zinc-300"}`}>
                      {daysLeft} Tag{daysLeft !== 1 ? "e" : ""} verbleibend
                    </div>
                  )}
                </div>
              </div>
              {(!data.subscription_status || data.subscription_status === "expired") && (
                <Link to="/checkout" data-testid="mybilling-renew-btn" className="mt-4 inline-flex items-center gap-2 bg-[#00E575] text-[#09090B] font-bold px-4 h-11 rounded-xl active:scale-[0.97] glow-green">
                  <Sparkles size={16} /> Jetzt aktivieren
                </Link>
              )}
            </div>

            {/* Auto-approval card */}
            {(data.auto_approval_notes?.length > 0 || data.auto_approval_fails?.length > 0) && (
              <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-4">
                <h3 className="font-display text-sm font-semibold mb-3 flex items-center gap-2">
                  <RotateCw size={14} className="text-[#00E575]" /> Plausibilitäts-Prüfung
                </h3>
                {data.user_status === "active" ? (
                  <div className="text-[12px] text-[#00E575] flex items-center gap-2 mb-2 font-medium">
                    <CheckCircle2 size={14} /> Konto automatisch freigeschaltet
                  </div>
                ) : (
                  <div className="text-[12px] text-amber-300 flex items-center gap-2 mb-2 font-medium">
                    <AlertTriangle size={14} /> Manuelle Sichtung durch Admin erforderlich
                  </div>
                )}
                <ul className="text-[12px] text-zinc-400 space-y-1 leading-relaxed">
                  {data.auto_approval_notes?.map((n, i) => <li key={`n-${i}`}>{n}</li>)}
                  {data.auto_approval_fails?.map((f, i) => <li key={`f-${i}`} className="text-amber-300">✗ {f.replace(/_/g, " ")}</li>)}
                </ul>
              </div>
            )}

            {/* Transactions */}
            <h3 className="font-display text-lg font-semibold mb-3 mt-6 flex items-center gap-2">
              <Receipt size={16} className="text-[#00E575]" /> Transaktionen
            </h3>
            {data.transactions?.length === 0 ? (
              <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-6 text-center text-zinc-400 text-sm font-medium">Noch keine Zahlungen.</div>
            ) : (
              <div className="space-y-2">
                {data.transactions?.map((t) => (
                  <div key={t.session_id} data-testid={`tx-${t.session_id}`} className="bg-[#18181B] border border-zinc-800 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-display font-bold text-lg">{EUR(t.amount_eur)}</span>
                      <span className={`text-[12px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                        t.payment_status === "paid" ? "bg-[#00E575]/15 text-[#00E575]" : "bg-amber-500/15 text-amber-300"
                      }`}>{t.payment_status || "pending"}</span>
                    </div>
                    <p className="text-[12px] text-zinc-400 leading-snug">{t.breakdown}</p>
                    {t.promo_code && (
                      <p className="text-[12px] text-amber-300 font-mono mt-1">Promo: {t.promo_code}</p>
                    )}
                    <p className="text-[12px] text-zinc-400 font-mono mt-1.5">{DT(t.created_at)}</p>
                  </div>
                ))}
              </div>
            )}

            <p className="text-[12px] text-zinc-300 mt-6 italic">
              Bei Fragen zu deiner Zahlung kontaktiere bitte den Support — wir senden dir die Rechnungen per E-Mail.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
